import { useQuery } from "@tanstack/react-query";
import { playersApi } from "../../api/players";
import { normalizeId, type MaybeId } from "./queryKeyUtils";

export const playerKeys = {
  all: ["players"] as const,
  byLadder: (ladderId: MaybeId) => ["players", "byLadder", normalizeId(ladderId)] as const,
  allForOrganizer: (ladderId: MaybeId) => ["players", "allForOrganizer", normalizeId(ladderId)] as const,
  detail: (id: MaybeId) => ["players", "detail", normalizeId(id)] as const,
  ladderStats: (ladderId: MaybeId, year: number | null | undefined) =>
    ["players", "ladderStats", normalizeId(ladderId), year ?? null] as const,
};

export const usePlayer = (id: MaybeId) =>
  useQuery({
    queryKey: playerKeys.detail(id),
    queryFn: () => playersApi.getById(id!),
    enabled: id != null,
  });

export const usePlayersByLadder = (ladderId: MaybeId) =>
  useQuery({
    queryKey: playerKeys.byLadder(ladderId),
    queryFn: () => playersApi.listByLadder(Number(ladderId)),
    enabled: ladderId != null,
  });

/**
 * Full roster of players — used by PlayerPicker when creating a tournament. Any logged-in user
 * can call this; the backend enforces auth. The ladderId is only used to pick the right ELO
 * for display (setPlayerElo runs on each row).
 */
export const useAllPlayersForOrganizer = (ladderId: MaybeId) =>
  useQuery({
    queryKey: playerKeys.allForOrganizer(ladderId),
    queryFn: () => playersApi.listAllForOrganizer(Number(ladderId)),
    enabled: ladderId != null,
  });

export const useLadderStats = (ladderId: MaybeId, year: number | null | undefined) =>
  useQuery({
    queryKey: playerKeys.ladderStats(ladderId, year),
    queryFn: () => playersApi.getLadderStats(Number(ladderId), year),
    enabled: ladderId != null,
  });
